import React, { useState } from "react";
import { useUser } from "../../contexts/UserContext";
import Layout from "../../components/Layout";
import { useNavigate } from "react-router-dom";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import ErrorModal from "../../components/ErrorModal";

const ResetPasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { authUser } = useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [modalText, setModalText] = useState("");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!currentPassword) {
      setModalText("既存のパスワードを入力してください。");
      setConfirmAction(() => () => {});
      setErrorModalOpen(true);
      return;
    }

    if (password !== confirmPassword) {
      setModalText("新しいパスワードと確認用パスワードが一致しません。");
      setConfirmAction(() => () => {});
      setErrorModalOpen(true);
      return;
    }

    setModalText("本当に保存しますか？");
    setConfirmAction(() => async () => {
      setIsLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/main/change-password`,
          {
            currentPassword,
            newPassword: password,
          },
          { withCredentials: true }
        );

        if (response.status === 200) {
          setSuccessModalOpen(true);
          setCurrentPassword("");
          setPassword("");
          setConfirmPassword("");
        } else {
          throw new Error("予期しないエラーが発生しました。");
        }
      } catch (error: any) {
        let errorMessages = "";

        if (error.response?.data?.errors) {
          Object.values(error.response.data.errors).forEach((messages) => {
            errorMessages += (messages as string[]).join("\n");
          });
        } else {
          errorMessages =
            error.response?.data?.error || "サーバーエラーが発生しました。";
        }

        setModalText(errorMessages);
        setErrorModalOpen(true);
      } finally {
        setIsLoading(false);
      }
    });
    setConfirmModalOpen(true);
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const userId = authUser?.id;
    if (userId) {
      navigate(`/main/profile/${userId}`);
    } else {
      alert("ログインしていないか、ユーザーIDが取得できません");
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-center">
        <div className="p-10 bg-white rounded-3xl w-[90%] max-w-2xl">
          {isLoading ? (
            <div className="animate-pulse">
              <div className="flex mb-4">
                <div className="border-l-8 rounded-3xl border-slate-100 mr-4"></div>
                <div className="h-6 bg-slate-200 rounded w-32"></div>
              </div>
              <div className="space-y-4">
                <div className="h-10 bg-slate-200 rounded"></div>
                <div className="h-10 bg-slate-200 rounded"></div>
                <div className="h-10 bg-slate-200 rounded"></div>
                <div className="flex gap-x-4 justify-center">
                  <div className="bg-slate-200 rounded-3xl py-2 px-4"></div>
                  <div className="bg-slate-200 rounded-3xl py-2 px-4"></div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex mb-4">
                <div className="title-text font-black">パスワードの変更</div>
                <div className="text-sm text-slate-500 mt-2 ms-4">
                  ※初回ログイン時はパスワードを変更してください。
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    既存のパスワード
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="既存のパスワード"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    新しいパスワード
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="新しいパスワード"
                  />
                  <p className="text-sm text-gray-500 text-center">
                    ※パスワードは、大文字・小文字・数字・記号を含む10文字以上である必要があります。
                  </p>
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    新しいパスワード（確認）
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="パスワード（確認）"
                  />
                </div>
                <div className="flex gap-x-4 justify-center">
                  <button
                    onClick={handleCancel}
                    className="bg-gray-300 text-black font-bold py-2 px-4 rounded-3xl hover:bg-gray-400"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 transition duration-200"
                  >
                    <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                    保存
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="確認"
        text={modalText}
        onConfirm={() => {
          setConfirmModalOpen(false);
          confirmAction();
        }}
        onCancel={() => setConfirmModalOpen(false)}
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        title="成功"
        text="パスワードが変更されました。"
        onClose={() => setSuccessModalOpen(false)}
      />
      <ErrorModal
        isOpen={isErrorModalOpen}
        title="エラー"
        text={modalText}
        onClose={() => setErrorModalOpen(false)}
      />
    </Layout>
  );
};

export default ResetPasswordPage;
